import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * EventTarget-based Navigation System
 * Handles the "Brain" (Backend) -> "Body" (Frontend) navigation signals.
 */

// 1. Strict Types matching your new Backend NavigationService response
export interface NavigationEventDetail {
    actionType: 'navigate' | 'openModal' | 'inChat';
    route?: string;       // e.g. "/client/123"
    modalName?: string;   // e.g. "ClientModal"
    modalParams?: any;    // e.g. { mode: 'create' }
    guidance?: string;    // e.g. "Opening client modal..."
    state?: any;
}

class NavigationEvent extends Event {
    detail: NavigationEventDetail;
    constructor(detail: NavigationEventDetail) {
        super('prospo:navigation');
        this.detail = detail;
    }
}

// Global Singleton for reliable event dispatching
export const navigationBus = new EventTarget();

// Helper to trigger navigation manually if needed
export const triggerNavigation = (detail: NavigationEventDetail) => {
    navigationBus.dispatchEvent(new NavigationEvent(detail));
};

export const useNavigationHandler = (options?: { onModalOpen?: (name: string, params: any) => void }) => {
    const navigate = useNavigate();
    const [isNavigating, setIsNavigating] = useState(false);

    // 2. The Listener - Reacts to signals from the Bus
    const handleEvent = useCallback((e: Event) => {
        const detail = (e as NavigationEvent).detail;
        console.log('🧭 AI Navigation Signal Received:', detail);
        setIsNavigating(true);

        try {
            // Handle Route Navigation
            if (detail.actionType === 'navigate' && detail.route) {
                // [UPDATE] Pass 'state' to the router
                // This allows the ClientPage to see { autoOpenModal: 'LinkContactModal' }
                navigate(detail.route, { state: detail.state });
            }

            // Handle Modals
            if (detail.actionType === 'openModal' && detail.modalName) {
                if (options?.onModalOpen) {
                    options.onModalOpen(detail.modalName, detail.modalParams);
                }
            }

            // 'inChat' actions are just informational, no navigation needed
        } catch (error) {
            console.error('Navigation failed:', error);
        } finally {
            setTimeout(() => setIsNavigating(false), 500);
        }
    }, [navigate, options]);

    // Attach listener on mount
    useEffect(() => {
        navigationBus.addEventListener('prospo:navigation', handleEvent);
        return () => {
            navigationBus.removeEventListener('prospo:navigation', handleEvent);
        };
    }, [handleEvent]);

    // 3. The Processor - Parses the new "toolOperations" from Backend
    // Pass this function your API response.toolOperations
    const processToolOperations = useCallback((toolOperations: any[]) => {
        if (!toolOperations || !Array.isArray(toolOperations)) return;

        // Look for the specific 'navigate_app' tool we defined in the Backend
        const navOp = toolOperations.find(op =>
            op.tool === 'navigate_app' && op.result && op.result.success
        );

        if (navOp) {
            console.log('🎯 Found Navigation Operation:', navOp.result);
            triggerNavigation(navOp.result);
        }
    }, []);

    return {
        processToolOperations,
        triggerNavigation,
        isNavigating
    };
};